import React from "react";
import * as Icon from "react-feather";

import JaneReportService from "../../../../services/JaneReportService";
import UtilityService from "../../../../services/UtilityService";

import WComponent from "../../../common/WComponent";
import Loader from "../../../common/loader/Loader";

import ToolsCollapsiblePanel from "../kit/ToolsCollapsiblePanel";
import ToolsMappingRow from "../kit/ToolsMappingRow";
import ToolsNotice from "../kit/ToolsNotice";
import ToolsProgressBadge from "../kit/ToolsProgressBadge";

import DeparturesPanel from "./DeparturesPanel";
import JournalEntriesPanel from "./JournalEntriesPanel";
import JournalProofPanel from "./JournalProofPanel";
import ReportSourcesPanel from "./ReportSourcesPanel";
import JournalIssues from "./JournalIssues";
import { describeCorrespondenceSource } from "./correspondenceSource";

import "./correspondence-panel.css";
import "./journal-panel.css";

class JournalPanel extends WComponent {
	constructor(props) {
		super(props);

		this.state = {
			isLoading: true,
			isSaving: false,
			accountRoles: [],
			quickBooksAccounts: [],
			taxCodeRoles: [],
			quickBooksTaxCodes: [],
			taxCodeSuggestions: [],
			journal: null,
			errorMessage: "",
			accountsMessage: "",
			suggestions: [],
			journalsExamined: 0
		};
	}

	taxTheReportsShow = () => {
		const { revenueRows } = this.props;
		return revenueRows ? revenueRows.totalTax : 0;
	};

	isTaxCharged = () => {
		return this.taxTheReportsShow() !== 0;
	};

	rateTheReportsImply = () => {
		const { revenueRows } = this.props;

		if (!revenueRows) {
			return null;
		}

		const { totalTax, taxableNetTotal } = revenueRows;
		const canDivide = Boolean(taxableNetTotal);

		return canDivide ? (totalTax / taxableNetTotal) * 100 : null;
	};

	async componentDidMount() {
		await this.loadMappingAndJournal();
	}

	formatMoney = ({ amount }) => UtilityService.formatMoney({ value: amount });

	loadMappingAndJournal = async () => {
		const { clientId } = this.props;

		await this.update({ isLoading: true, errorMessage: "" });

		const mappingResult = await JaneReportService.listAccountMappings({ clientId });

		if (mappingResult.error) {
			await this.update({ isLoading: false, errorMessage: mappingResult.error });
			return;
		}

		const accountsResult = await JaneReportService.listQuickBooksAccounts({ clientId });
		const couldNotReadAccounts = Boolean(accountsResult.error);
		const quickBooksAccounts = couldNotReadAccounts ? [] : accountsResult.accounts;

		const suggestionResult = await JaneReportService.suggestAccountMappings({ clientId, figures: this.props.figures });
		const suggestions = suggestionResult.error ? [] : suggestionResult.suggestions;

		const taxCodeResult = this.isTaxCharged()
			? await JaneReportService.listQuickBooksTaxCodes({ clientId, impliedRatePercent: this.rateTheReportsImply() })
			: { taxCodes: [], suggestions: [] };

		await this.update({
			accountRoles: mappingResult.roles,
			quickBooksAccounts,
			taxCodeRoles: mappingResult.taxCodeRoles,
			quickBooksTaxCodes: taxCodeResult.error ? [] : taxCodeResult.taxCodes,
			taxCodeSuggestions: taxCodeResult.error ? [] : taxCodeResult.suggestions,
			suggestions,
			journalsExamined: suggestionResult.journalsExamined || 0,
			accountsMessage: couldNotReadAccounts ? accountsResult.error : ""
		});
		await this.buildJournal();
	};

	buildJournal = async () => {
		const { clientId, figures, statedPriorReceivable, statedReceivableAsAt } = this.props;

		const journal = await JaneReportService.buildJournal({
			clientId,
			figures,
			priorPeriodReceivable: statedPriorReceivable,
			receivableAsAt: statedReceivableAsAt
		});

		if (journal.error) {
			await this.update({ isLoading: false, errorMessage: journal.error });
			return;
		}

		await this.update({ isLoading: false, journal, accountRoles: journal.accountRoles, taxCodeRoles: journal.taxCodeRoles });
	};

	saveMappings = async ({ mappings }) => {
		const { clientId } = this.props;

		await this.update({ isSaving: true });

		const saved = await JaneReportService.saveAccountMappings({ clientId, mappings });

		if (saved.error) {
			UtilityService.alert({ type: "error", text: saved.error });
			await this.update({ isSaving: false });
			return;
		}

		await this.update({ isSaving: false, accountRoles: saved.roles, taxCodeRoles: saved.taxCodeRoles });
		await this.buildJournal();
	};

	onReferenceSelected = async ({ roleKey, qboReferenceId, qboReferenceName }) => {
		await this.saveMappings({ mappings: [{ role: roleKey, qboReferenceId, qboReferenceName }] });
	};

	onSuggestionAccepted = async ({ suggestion }) => {
		await this.onReferenceSelected({
			roleKey: suggestion.role,
			qboReferenceId: suggestion.qboReferenceId,
			qboReferenceName: suggestion.qboReferenceName
		});
	};

	untakenSuggestions = () => {
		const { suggestions, taxCodeSuggestions, taxCodeRoles } = this.state;

		const neededAccountRoles = this.neededAccountRoles();
		const rolesStillToChoose = this.isTaxCharged() ? [...neededAccountRoles, ...taxCodeRoles] : neededAccountRoles;
		const keysStillToChoose = rolesStillToChoose.filter(role => !role.qboReferenceId).map(role => role.key);
		const everySuggestion = this.isTaxCharged() ? [...suggestions, ...taxCodeSuggestions] : suggestions;

		return everySuggestion.filter(suggestion => keysStillToChoose.includes(suggestion.role));
	};

	onAllSuggestionsAccepted = async () => {
		const toApply = this.untakenSuggestions();

		if (toApply.length === 0) {
			return;
		}

		const mappings = toApply.map(suggestion => ({
			role: suggestion.role,
			qboReferenceId: suggestion.qboReferenceId,
			qboReferenceName: suggestion.qboReferenceName
		}));

		await this.saveMappings({ mappings });
	};

	renderReferenceRow = ({ role, options, suggestion, placeholder, searchPlaceholder, certaintyLabel, sublabel }) => {
		const { isSaving } = this.state;

		const offered = suggestion ? { value: suggestion.qboReferenceId, label: suggestion.qboReferenceName, evidence: suggestion.evidence, isCertain: suggestion.isCertain, certaintyLabel } : null;

		return (
			<ToolsMappingRow
				key={role.key}
				isChosen={Boolean(role.qboReferenceId)}
				label={role.label}
				sublabel={sublabel}
				options={options}
				value={role.qboReferenceId}
				placeholder={placeholder}
				searchPlaceholder={searchPlaceholder}
				disabled={isSaving}
				onChange={qboReferenceId => {
					const chosen = options.find(option => option.value === qboReferenceId);
					this.onReferenceSelected({ roleKey: role.key, qboReferenceId, qboReferenceName: chosen ? chosen.name : null });
				}}
				suggestion={offered}
				onAcceptSuggestion={() => this.onSuggestionAccepted({ suggestion })}
			/>
		);
	};

	renderAccountRow = ({ role }) => {
		const { quickBooksAccounts, suggestions } = this.state;

		const options = quickBooksAccounts.map(account => ({ value: account.id, name: account.fullyQualifiedName, label: account.fullyQualifiedName }));
		const suggestion = suggestions.find(candidate => candidate.role === role.key);
		const correspondenceSourceText = describeCorrespondenceSource({ fromCorrespondence: role.fromCorrespondence });
		const correspondenceSourceLine = correspondenceSourceText ? <span className="jane-correspondence-source">{correspondenceSourceText}</span> : null;

		return this.renderReferenceRow({
			role,
			options,
			suggestion,
			placeholder: "Choose a QuickBooks account…",
			searchPlaceholder: "Search accounts…",
			certaintyLabel: "exact match",
			sublabel: correspondenceSourceLine
		});
	};

	renderTaxCodeRow = ({ role }) => {
		const { quickBooksTaxCodes, taxCodeSuggestions } = this.state;

		const options = quickBooksTaxCodes.map(taxCode => ({
			value: taxCode.id,
			name: taxCode.name,
			label: taxCode.salesRatePercent > 0 ? `${taxCode.name} — charges ${taxCode.salesRatePercent}%` : `${taxCode.name} — charges nothing`
		}));
		const suggestion = taxCodeSuggestions.find(candidate => candidate.role === role.key);

		return this.renderReferenceRow({
			role,
			options,
			suggestion,
			placeholder: "Choose a QuickBooks tax code…",
			searchPlaceholder: "Search tax codes…",
			certaintyLabel: "rate agrees"
		});
	};

	renderTaxabilityCounts = () => {
		const { revenueRows } = this.props;

		if (!revenueRows) {
			return null;
		}

		const { taxedRowCount, untaxedRowCount, taxableNetTotal } = revenueRows;

		return (
			<div className="journal-panel__taxability">
				<div className="journal-panel__taxability-figures">
					<div className="journal-panel__taxability-figure">
						<span className="journal-panel__taxability-value tk-amount">{taxedRowCount}</span>
						<span className="journal-panel__taxability-caption tk-text-muted">
							lines taxed · {UtilityService.formatMoney({ value: taxableNetTotal, emptyText: "—" })}
						</span>
					</div>
					<div className="journal-panel__taxability-figure">
						<span className="journal-panel__taxability-value tk-amount">{untaxedRowCount}</span>
						<span className="journal-panel__taxability-caption tk-text-muted">lines untaxed</span>
					</div>
				</div>
				<div className="tk-note">
					Worth a glance if this clinic sells anything taxable. A taxable item discounted to nothing carries no tax, so it counts as exempt here and
					the taxable sales on the return come out lower than they should. The money in the journal is right either way.
				</div>
			</div>
		);
	};

	renderClearingCheck() {
		const { journal } = this.state;
		const hasClearing = Boolean(journal && journal.clearing);

		if (!hasClearing) {
			return null;
		}

		const { clearing, openingBalances } = journal;
		const accountsHeldAtOpening = openingBalances ? [openingBalances.transit, openingBalances.receivable].filter(Boolean) : [];
		const linesOutOfClearing = [journal.basis.booksRefunds && "refunds", "fees", journal.wiring.receivable && "the receivable"].filter(Boolean);
		const outOfClearingLabel = `Out of it — ${linesOutOfClearing.slice(0, -1).join(", ")}${linesOutOfClearing.length > 1 ? " and " : ""}${linesOutOfClearing.slice(-1)}`;

		return (
			<div className="tk-panel">
				<div className="tk-panel__head">
					<div className="tk-section-label">Does the clearing account clear?</div>
				</div>

				<div className="journal-panel__clearing">
					<div className="journal-panel__clearing-row">
						<span>Into the clearing account this period</span>
						<span className="tk-amount">{this.formatMoney({ amount: clearing.debited })}</span>
					</div>
					<div className="journal-panel__clearing-row">
						<span>{outOfClearingLabel}</span>
						<span className="tk-amount">{this.formatMoney({ amount: clearing.credited })}</span>
					</div>
					<div className="journal-panel__clearing-row journal-panel__clearing-row--total">
						<span>Left waiting for cash</span>
						<span className="tk-amount">{this.formatMoney({ amount: clearing.residual })}</span>
					</div>
					<div className="journal-panel__clearing-row">
						<span className="tk-text-muted">Cash the reports say was collected</span>
						<span className="tk-amount tk-text-muted">{this.formatMoney({ amount: clearing.cashExpected })}</span>
					</div>
				</div>

				{openingBalances && (
					<div className="journal-panel__opening">
						<div className="journal-panel__opening-title">What these accounts already held at {openingBalances.asAt}</div>
						{accountsHeldAtOpening.map(account => (
							<div className="journal-panel__clearing-row" key={account.accountName}>
								<span>{account.accountName}</span>
								<span className="tk-amount">
									{account.wasFound ? this.formatMoney({ amount: account.balance }) : <span className="tk-text-muted">no activity to this date</span>}
								</span>
							</div>
						))}
						<div className="journal-panel__opening-note tk-text-muted">
							A clearing account empties as the cash it is waiting for arrives. One carrying a large balance from earlier periods is a process
							that stopped clearing — and the books still balance while it happens, because the stranded amount is matched by an equal and
							opposite amount elsewhere.
						</div>
					</div>
				)}
			</div>
		);
	}

	neededAccountRoles = () => {
		const { accountRoles } = this.state;
		const neededRoles = accountRoles.filter(role => role.isNeeded !== false);
		return neededRoles;
	};

	render() {
		const { isLoading, isSaving, taxCodeRoles, journal, errorMessage, accountsMessage, journalsExamined } = this.state;
		const { statedPriorReceivable, statedReceivableAsAt, onPriorReceivableStated, onReceivableAsAtStated } = this.props;

		if (isLoading) {
			return (
				<div className="tk-panel tk-panel-loading">
					<Loader />
					<span className="tk-text-muted">Building the journal…</span>
				</div>
			);
		}

		const isTaxCharged = this.isTaxCharged();
		const hasEntries = Boolean(journal && journal.entries.length > 0);

		const neededAccountRoles = this.neededAccountRoles();
		const rolesToChoose = isTaxCharged ? [...neededAccountRoles, ...taxCodeRoles] : neededAccountRoles;
		const stillToChoose = rolesToChoose.filter(role => !role.qboReferenceId).length;
		const choicesInTotal = rolesToChoose.length;

		const usableSuggestions = this.untakenSuggestions();
		const hasUsableSuggestions = usableSuggestions.length > 0;

		return (
			<>
				<ToolsCollapsiblePanel
					title="Where this journal posts"
					defaultOpen={stillToChoose > 0}
					status={<ToolsProgressBadge remainingCount={stillToChoose} totalCount={choicesInTotal} doneWord="chosen" todoWord="choose" />}
				>
					{hasUsableSuggestions && (
						<div className="tk-suggest-bar">
							<div className="tk-suggest-bar__text">
								{journalsExamined > 0
									? `Read from ${journalsExamined} previous Jane ${journalsExamined === 1 ? "journal" : "journals"} and this client's QuickBooks setup. Check each before accepting — a wrong choice here posts real money to the wrong place.`
									: "Check each suggestion before accepting — a wrong choice here posts real money to the wrong place."}
							</div>
							<button className="tk-button tk-button--secondary tk-button--compact" onClick={this.onAllSuggestionsAccepted} type="button">
								Use all {usableSuggestions.length}
							</button>
						</div>
					)}

					{accountsMessage && (
						<div className="journal-panel__accounts-message tk-text-muted">
							<Icon.Info size={14} />
							<span>{accountsMessage} — the pickers below stay empty until this client is connected.</span>
						</div>
					)}

					<div>{neededAccountRoles.map(role => this.renderAccountRow({ role }))}</div>

					{isTaxCharged && (
						<div className="journal-panel__group">
							<div className="journal-panel__group-head">
								<span className="tk-section-label">Tax codes</span>
								<span className="journal-panel__group-note tk-text-muted">
									{UtilityService.formatMoney({ value: this.taxTheReportsShow(), emptyText: "No" })} of tax on these reports
								</span>
							</div>
							<div className="journal-panel__group-lede tk-text-muted">
								Each revenue line carries one of these codes so QuickBooks works out what is owed — the amount is never typed in. A line counts
								as taxable when the report charged tax on it, so no list of taxable services has to be kept anywhere.
							</div>
							<div>{taxCodeRoles.map(role => this.renderTaxCodeRow({ role }))}</div>
							{this.renderTaxabilityCounts()}
						</div>
					)}

					{errorMessage && <div className="journal-panel__error tk-text-muted">{errorMessage}</div>}
				</ToolsCollapsiblePanel>

				<JournalIssues
					issues={journal ? journal.blockingIssues : []}
					isSaving={isSaving}
					statedPriorReceivable={statedPriorReceivable}
					statedReceivableAsAt={statedReceivableAsAt}
					onPriorReceivableStated={onPriorReceivableStated}
					onReceivableAsAtStated={onReceivableAsAtStated}
				/>

				{journal && journal.alreadyInQuickBooks && journal.alreadyInQuickBooks.length > 0 && (
					<ToolsNotice
						tone="warning"
						icon={<Icon.AlertTriangle size={16} />}
						title="QuickBooks already holds a Jane journal for this month"
						detail={`${journal.alreadyInQuickBooks.map(posted => `${posted.docNumber || "A journal with no number"} on ${posted.date}`).join("; ")}. Posting this one as well would record the month twice. The journal below is shown so it can be compared, not posted.`}
					/>
				)}

				{hasEntries && <ReportSourcesPanel reportSources={journal.reportSources} uploadedReports={this.props.uploadedReports} />}

				{hasEntries && <JournalProofPanel proof={journal.proof} revenueTable={journal.revenueTable} siteAttribute={journal.siteAttribute} />}

				{hasEntries && <DeparturesPanel clientId={this.props.clientId} built={journal} periodEnd={journal.periodEnd} />}

				{this.renderClearingCheck()}

				{hasEntries && <JournalEntriesPanel journal={journal} />}
			</>
		);
	}
}

export default JournalPanel;
